import { useTranslation } from 'react-i18next';

import { Icon } from '@sdb/planning-ui';

import RuleSetCard, { UrgencyChip } from 'components/shared/RuleSetCard';
import { convertToFormattedDate } from 'utils/date';
import PrescriptionRulesGrid from './PrescriptionRulesGrid';

import type { PrescriptionResponse, PrescriptionStatus } from 'generated/index';
import type { RuleSetCardStatus } from 'components/shared/RuleSetCard';

interface PrescriptionCardProps {
  prescription: PrescriptionResponse;
  dateInterval?: { startDate: Date; endDate: Date };
}

const CARD_STATUS: Record<PrescriptionStatus, RuleSetCardStatus> = {
  ACTIVE: 'active',
  CONCEPT: 'concept',
  EXPIRED: 'expired'
};

const formatDate = (date?: string) => {
  if (!date) return '?';
  return convertToFormattedDate(date);
};

const PrescriptionCard = ({
  prescription,
  dateInterval
}: PrescriptionCardProps) => {
  'use memo';
  const { t } = useTranslation('common', {
    keyPrefix: 'careReceiversHome.prescriptions'
  });

  const ruleCount = prescription.prescriptionRules?.length ?? 0;
  const hasStartDate = !!prescription.startDate;
  const hasRules = ruleCount > 0 && hasStartDate;

  return (
    <li>
      <RuleSetCard
        title={prescription.name}
        status={CARD_STATUS[prescription.status]}
        statusLabel={t(`status.${prescription.status.toLowerCase()}`)}
        badges={
          prescription.urgency === 'URGENT' && (
            <UrgencyChip
              icon={<Icon type="triangle-exclamation" fontSize="inherit" />}
              label={t('urgency.urgent')}
              size="small"
              variant="outlined"
            />
          )
        }
        metaItems={[
          {
            key: 'period',
            icon: 'calendar',
            content: hasStartDate ? (
              `${formatDate(prescription.startDate)} - ${formatDate(
                prescription.endDate
              )}`
            ) : (
              <em>{t('noStartDate')}</em>
            )
          },
          {
            key: 'rules',
            icon: 'clock',
            content: `${ruleCount} ${t('rules', { count: ruleCount })}`
          }
        ]}
        collapsible
      >
        {hasRules && (
          <PrescriptionRulesGrid
            prescription={prescription}
            dateInterval={dateInterval}
          />
        )}
      </RuleSetCard>
    </li>
  );
};

export default PrescriptionCard;
